/**
 * usePlaylistSyncToasts Hook
 *
 * Listens to playlist sync events and shows toast notifications
 * for errors and important events.
 */

import { useEffect, useCallback } from 'react';
import { useToast } from '../components/ui';
import {
  addSyncEventListener,
  removeSyncEventListener,
  type PlaylistSyncOperation,
  type PlaylistSyncEventListener,
} from '../services/playlistSync';

/**
 * Hook that subscribes to playlist sync events and shows toast notifications.
 * Should be called once at the app level (e.g., in a layout component).
 */
export function usePlaylistSyncToasts(): void {
  const { addToast } = useToast();

  // Memoize the event handlers
  const handleOperationFailed = useCallback(
    (operation: PlaylistSyncOperation, error: string) => {
      // Show user-friendly error messages
      const action = operation.type === 'add' ? 'add to' : 'remove from';
      const serviceName = operation.service === 'spotify' ? 'Spotify' : 'music service';

      // Check for specific error types
      if (error.includes('authorization') || error.includes('expired')) {
        addToast(`${serviceName} authorization expired. Please reconnect in Settings.`, {
          type: 'error',
          duration: 6000,
        });
      } else if (error.includes('rate limit')) {
        addToast(`${serviceName} is busy. Will retry automatically.`, {
          type: 'warning',
        });
      } else {
        addToast(`Failed to ${action} playlist: ${error}`, {
          type: 'error',
        });
      }
    },
    [addToast]
  );

  useEffect(() => {
    const listener: PlaylistSyncEventListener = {
      onOperationFailed: handleOperationFailed,
      // We don't show success toasts by default to avoid notification fatigue
      // onOperationComplete is available if needed
    };

    addSyncEventListener(listener);

    return () => {
      removeSyncEventListener(listener);
    };
  }, [handleOperationFailed]);
}
